import { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { FileDown, Loader2, ArrowRight, CreditCard, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { LandRatingCard } from "../components/LandRatingCard";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

export default function PublicReportPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [rating, setRating] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [paying, setPaying] = useState(null);
  const [tier, setTier] = useState("standard");
  const [config, setConfig] = useState({ stripe_enabled: true, razorpay_enabled: false });

  useEffect(() => {
    api.get(`/public/rating/${id}`)
      .then(({ data }) => setRating(data))
      .catch(() => setNotFound(true));
    api.get("/payments/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (params.get("payment") === "cancelled") {
      toast.info("Payment cancelled — you can try again anytime.");
    }
  }, [params]);

  const payWithStripe = async () => {
    setPaying("stripe");
    try {
      const { data } = await api.post("/payments/checkout", {
        inquiry_id: id,
        origin_url: window.location.origin,
        tier,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start payment");
      setPaying(null);
    }
  };

  const payWithRazorpay = async () => {
    setPaying("razorpay");
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Could not load Razorpay");
      const { data } = await api.post("/payments/razorpay/order", { inquiry_id: id, tier });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: data.name,
        description: data.description,
        theme: { color: "#113835" },
        modal: { ondismiss: () => setPaying(null) },
        handler: async (res) => {
          try {
            const { data: v } = await api.post("/payments/razorpay/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            navigate(`/payment/success?session_id=${v.session_id}`);
          } catch {
            toast.error("Payment verification failed — please contact us if you were charged.");
            setPaying(null);
          }
        },
      });
      rzp.on("payment.failed", () => {
        toast.error("Payment failed — no amount was captured. Please try again.");
        setPaying(null);
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Could not start payment");
      setPaying(null);
    }
  };

  if (notFound) {
    return (
      <div data-testid="report-not-found" className="py-32 bg-white text-center px-6">
        <p className="tt-eyebrow mb-4 justify-center flex">Rating Not Published</p>
        <h1 className="font-heading text-3xl md:text-4xl font-medium text-[#113835]">This property hasn't been rated yet.</h1>
        <p className="mt-4 text-gray-500 font-light">Request a property verification and our experts will rate it for you.</p>
        <Link to="/request-title-search" data-testid="report-request-cta" className="tt-btn-gold mt-10 inline-flex">
          Request Property Verification <ArrowRight size={15} strokeWidth={1.5} />
        </Link>
      </div>
    );
  }

  if (!rating) {
    return (
      <div data-testid="report-loading" className="py-40 flex justify-center">
        <Loader2 size={28} className="animate-spin text-[#C1A74A]" />
      </div>
    );
  }

  return (
    <div data-testid="public-report-page">
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <p className="tt-eyebrow mb-4">Terraa Land Rating · TT-{rating.ref}</p>
            <h1 className="font-heading text-3xl md:text-5xl font-medium text-[#113835] tracking-tight">
              Your property has been rated by our legal experts.
            </h1>
            <p className="mt-6 text-base text-gray-600 font-light leading-relaxed">
              The rating card summarises title history, encumbrances, zoning verification, and tax compliance for
              <span className="font-medium text-[#113835]"> {rating.property_label}</span>.
              The full verified PDF report includes every finding, government record checked, and examiner's notes.
            </p>

            <div className="mt-10 border border-[#113835]/12 p-8" data-testid="report-purchase-box">
              <p className="font-heading text-sm font-medium text-[#113835]">Get the Verified Report</p>
              <p className="mt-2 text-xs text-gray-500 font-light">Choose your report level — both are expert-verified and court & bank ready.</p>
              <div className="mt-5 space-y-3" data-testid="report-tier-options">
                {(rating.tiers || []).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`report-tier-${t.id}`}
                    onClick={() => setTier(t.id)}
                    className={`w-full text-left border p-4 transition-colors ${tier === t.id ? "border-[#113835] bg-[#F9FAFA]" : "border-[#113835]/15 hover:border-[#C1A74A]"}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2.5">
                        <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${tier === t.id ? "border-[#113835]" : "border-[#113835]/30"}`}>
                          {tier === t.id && <span className="w-2 h-2 rounded-full bg-[#113835]" />}
                        </span>
                        <span className="font-heading text-sm font-medium text-[#113835]">{t.label}</span>
                      </span>
                      <span className="font-heading text-base font-semibold text-[#113835]" data-testid={`report-tier-price-${t.id}`}>
                        ₹{Number(t.amount).toLocaleString("en-IN")}
                      </span>
                    </div>
                    <p className="mt-1.5 text-xs text-gray-500 font-light pl-[26px]">{t.description}</p>
                  </button>
                ))}
              </div>
              <div className="mt-6 space-y-3">
                {config.razorpay_enabled && (
                  <button
                    data-testid="report-pay-razorpay"
                    onClick={payWithRazorpay}
                    disabled={!!paying}
                    className="tt-btn-gold w-full justify-center disabled:opacity-60"
                  >
                    {paying === "razorpay" ? <Loader2 size={15} className="animate-spin" /> : <Wallet size={15} strokeWidth={1.5} />}
                    {paying === "razorpay" ? "Opening Razorpay…" : "Pay with UPI / Cards / NetBanking"}
                  </button>
                )}
                <button
                  data-testid="report-download-btn"
                  onClick={payWithStripe}
                  disabled={!!paying}
                  className={`${config.razorpay_enabled ? "tt-btn-outline" : "tt-btn-gold"} w-full justify-center disabled:opacity-60`}
                >
                  {paying === "stripe" ? <Loader2 size={15} className="animate-spin" /> : config.razorpay_enabled ? <CreditCard size={15} strokeWidth={1.5} /> : <FileDown size={15} strokeWidth={1.5} />}
                  {paying === "stripe" ? "Redirecting to secure payment…" : config.razorpay_enabled ? "Pay with Card (Stripe)" : "Download Verified PDF Report"}
                </button>
              </div>
              <p className="mt-3 text-[0.65rem] text-gray-400 text-center uppercase tracking-[0.14em] font-heading">
                Secure payment via {config.razorpay_enabled ? "Razorpay / Stripe" : "Stripe"}
              </p>
            </div>
          </div>
          <div className="pt-4">
            <LandRatingCard rating={rating} />
          </div>
        </div>
      </section>
    </div>
  );
}
